import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Checkbox,
  TextField,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { IOSSwitch } from '../IOSSwitch';
import { Loading } from '@bumble/app/components/Loading';

interface EditDataForwardingProps {
  onSubmit: () => void;
  cpeId: string;
}

export const EditDataForwarding = ({ onSubmit, cpeId }: EditDataForwardingProps) => {
  const api = useApi();

  const { isLoading, data } = useQuery(
    ['cellularConfig', cpeId],
    () => api.getCellularConfigApiV1GetCellularConfigGet({ cpe_id: cpeId }),
    {
      enabled: !!cpeId,
      staleTime: 0,
      refetchOnMount: true,
    }
  );

  const [newAllowDataFailover, setNewAllowDataFailOver] = useState<boolean>(true);
  const [allowSetting, setAllowSetting] = useState<boolean>(false);
  const [newApn, setNewApn] = useState<string>('');

  useEffect(() => {
    if (data?.data) {
      setNewAllowDataFailOver(data.data.allow_data_failover ?? true);
      setNewApn(data.data.apn ?? '');
      setAllowSetting(data.data.apn !== '');
    }
  }, [data]);

  const editDataForwardingPost = useMutation(api.configureCellularEndpointApiV1ConfigureCellularPost, {
    onSuccess() {
      toast.success('Updated Data Successfully');
      onSubmit();
    },
    onError: apiError,
  });

  const hasChanges =
    newAllowDataFailover !== data?.data.allow_data_failover ||
    newApn !== data?.data.apn ||
    allowSetting !== (data?.data.apn !== '');

  const handleSubmit = () => {
    editDataForwardingPost.mutate({
      cpe_id: cpeId,
      allow_data_failover: newAllowDataFailover,
      apn: allowSetting ? newApn : '',
    })
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAllowDataFailOver(e.target.checked);
  };

  if (isLoading) return <Loading />

  return (
    <Box pt={2}>
      <Box display="flex" alignItems='center' sx={{ my: 4 }} >
        <Typography sx={{fontWeight: 600}}>Allow Data Forwarding: </Typography>
        <FormControlLabel
          value="top"
          control={
            <IOSSwitch color="primary" checked={newAllowDataFailover} onChange={handleChange} />
          }
          label=''
          labelPlacement="bottom"
          sx={(theme) => ({ '&:hover': { color: theme.palette.primary.main } })}
          componentsProps={{
            typography: {
              sx: (theme) => ({
                fontSize: 12,
                color: !newAllowDataFailover ? theme.palette.grey[600] : theme.palette.primary.main,
              }),
            },
          }}
        />
      </Box>
      <FormControlLabel
        sx={{ mb: 4 }}
        control={
          <Checkbox
            checked={allowSetting}
            onChange={(e) => setAllowSetting(e.target.checked)}
          />
        }
        label="Advanced"
      />
      {allowSetting && (
        <TextField
          sx={{ mb: 4 }}
          fullWidth
          label="Access Point Name (APN)"
          variant="outlined"
          value={newApn}
          onChange={(e) => setNewApn(e.target.value)}
          autoFocus
        />
      )}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit} disabled={!hasChanges || editDataForwardingPost.isLoading}>
          Save
        </Button>
      </Box>
    </Box>
  );
};
